import React, { useEffect, useRef, useState } from "react";
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { process } from '@progress/kendo-data-query';
import { PrivateAxios } from "../../environment/AxiosInstance";
import { CurrencyList } from '../../utils/StaticData';
import { UserAuth } from '../auth/Auth';
import * as XLSX from 'xlsx';
import moment from "moment";
import '@progress/kendo-theme-default/dist/all.css';
import './SalesTargets.css';

const FILTER_DEBOUNCE_MS = 300;

export default function HQOverview() {
  const { MatchPermission } = UserAuth();

  const [rows, setRows] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState('INR');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emptyMessage, setEmptyMessage] = useState(null);
  const [targetType, setTargetType] = useState('amount');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dataState, setDataState] = useState({ skip: 0, take: 25, sort: [{ field: 'achievementPercentage', dir: 'desc' }] });
  const [drillDown, setDrillDown] = useState(null);

  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!MatchPermission(['Sales All'])) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchOverview(), FILTER_DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, fromDate, toDate, MatchPermission]);

  if (!MatchPermission(['Sales All'])) {
    return (
      <div className="p-4 text-center">
        <h4>Access Denied</h4>
        <p>HQ Overview is restricted to Sales All managers.</p>
      </div>
    );
  }

  function newAbortSignal() {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  }

  async function fetchOverview() {
    setLoading(true);
    setError(null);
    setEmptyMessage(null);
    const signal = newAbortSignal();
    try {
      const params = new URLSearchParams({
        targetType,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
      });
      const res = await PrivateAxios.get("/sales/reports/hq-overview?" + params, { signal });
      if (res.data && res.data.success) {
        const data = res.data.data || [];
        setRows(data);
        if (res.data.baseCurrency) setBaseCurrency(res.data.baseCurrency);
        if (data.length === 0) setEmptyMessage('No assignees with targets in this range.');
      } else {
        throw new Error('Failed to fetch HQ overview');
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED' || err.message === 'canceled') return;
      console.error('HQ overview error:', err);
      setError((err && err.message) || 'Failed to load HQ overview');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  const currencySymbol = (CurrencyList.find(c => c.code === baseCurrency) || {}).symbol || '';
  const formatValue = (value) => {
    if (targetType === 'amount') {
      const formatted = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
      return currencySymbol + formatted + " " + baseCurrency;
    }
    return (value || 0).toString();
  };

  const companyTotals = rows.reduce((acc, r) => ({
    target: acc.target + (Number(r.totalTarget) || 0),
    achievement: acc.achievement + (Number(r.totalAchievement) || 0),
    balance: acc.balance + (Number(r.balance) || 0),
    assigneesCount: acc.assigneesCount + 1,
  }), { target: 0, achievement: 0, balance: 0, assigneesCount: 0 });
  const companyPct = companyTotals.target > 0
    ? Number(((companyTotals.achievement / companyTotals.target) * 100).toFixed(2))
    : 0;

  const NameCell = (props) => {
    const { dataItem } = props;
    return (
      <td>
        <div className="fw-bold">{dataItem.name}</div>
        {dataItem.email && <div className="small text-muted">{dataItem.email}</div>}
      </td>
    );
  };

  const ValueCell = (props) => <td className="text-end">{formatValue(props.dataItem[props.field])}</td>;
  const PercentCell = (props) => {
    const p = Number(props.dataItem.achievementPercentage) || 0;
    const color = p >= 100 ? 'text-success' : p >= 75 ? 'text-info' : p >= 50 ? 'text-warning' : 'text-danger';
    return <td className={"text-end " + color + " fw-bold"}>{p}%</td>;
  };
  const DrillCell = (props) => (
    <td className="text-center">
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => setDrillDown(props.dataItem)}
        title="See per-target breakdown"
      >
        <i className="fas fa-list me-1"></i>Targets ({props.dataItem.targetsCount})
      </button>
    </td>
  );

  // Per-Assignee Overview Excel export.
  //
  // Single flat sheet so the user can filter/sort/pivot directly. Each row
  // carries the full parent context (assignee summary + target detail) and
  // the leaf daily-entry value, so every row is self-describing:
  //
  //   Assignee | Email | Asgn Total Target | Asgn Total Ach | Asgn Ach %
  //   | Target | Period | Window Start | Window End | Currency
  //   | Assigned (native) | Achieved (native)
  //   | Assigned (base) | Achieved (base) | Pending (base) | Performance %
  //   | Last Achievement Date
  //   | Daily Date | RNC | REC | Daily Total       (for amount targets)
  //   | Daily Date | Count                          (for number targets)
  //
  // A target with NO daily entries still produces one row (daily columns
  // left blank) so it is visible alongside targets that do have entries.
  const exportToExcel = () => {
    if (rows.length === 0) return;

    const summary = [
      ['HQ Overview Export'],
      ['Target Type', targetType === 'amount' ? 'Amount' : 'Number'],
      ['Base Currency', baseCurrency],
      ['Target Window From', fromDate || 'All'],
      ['Target Window To', toDate || 'All'],
      ['Company Total Target (' + baseCurrency + ')', Number(companyTotals.target) || 0],
      ['Company Total Achievement (' + baseCurrency + ')', Number(companyTotals.achievement) || 0],
      ['Company Total Balance (' + baseCurrency + ')', Number(companyTotals.balance) || 0],
      ['Overall Achievement %', companyPct],
      ['Assignees Count', Number(companyTotals.assigneesCount) || 0],
      ['Generated At', moment().format('YYYY-MM-DD HH:mm:ss')],
      [],
    ];

    // Column order: Daily Date first, then full parent context, then the
    // daily-entry numeric columns at the end.
    const headers = [
      'Daily Date',
      'Assignee', 'Email',
      'Assignee Total Target (' + baseCurrency + ')',
      'Assignee Total Achievement (' + baseCurrency + ')',
      'Assignee Achievement %',
      'Target', 'Period', 'Window Start', 'Window End', 'Currency',
      'Assigned (native)', 'Achieved (native)',
      'Assigned (' + baseCurrency + ')',
      'Achieved (' + baseCurrency + ')',
      'Pending (' + baseCurrency + ')',
      'Performance %', 'Last Achievement Date',
    ];
    if (targetType === 'number') {
      headers.push('Count');
    } else {
      headers.push('RNC', 'REC', 'Daily Total');
    }

    // To avoid duplicate text per row, assignee summary columns are only
    // populated on the FIRST row of each assignee block, and target context
    // columns are only populated on the FIRST row of each target block.
    // Subsequent daily-entry rows leave those parent columns blank so the
    // sheet reads as a grouped report while every row still references its
    // own daily date + leaf values.
    const blankASummary = ['', '', '', '', ''];
    const blankTCtx = ['', '', '', '', '', '', '', '', '', '', '', ''];
    const blankValues = targetType === 'number' ? [''] : ['', '', ''];

    const dataRows = [];
    rows.forEach(r => {
      const aSummaryFull = [
        r.name || '',
        r.email || '',
        Number(r.totalTarget) || 0,
        Number(r.totalAchievement) || 0,
        Number(r.achievementPercentage) || 0,
      ];
      const targets = r.targets || [];

      if (targets.length === 0) {
        // Assignee with no targets at all — single row showing the assignee
        // summary, all target + value columns blank.
        dataRows.push(['', ...aSummaryFull, ...blankTCtx, ...blankValues]);
        return;
      }

      let assigneeFirstRow = true;
      targets.forEach(t => {
        const assignedBase = Number(t.assignedBase) || 0;
        const achievedBase = Number(t.achievedBase) || 0;
        const pending = assignedBase - achievedBase;
        const perf = assignedBase > 0
          ? Number(((achievedBase / assignedBase) * 100).toFixed(2))
          : 0;
        const tCtxFull = [
          t.title || '', t.period || '',
          t.startDate ? moment(t.startDate).format('YYYY-MM-DD') : '',
          t.endDate ? moment(t.endDate).format('YYYY-MM-DD') : '',
          t.currency || '',
          Number(t.assignedNative) || 0,
          Number(t.achievedNative) || 0,
          assignedBase, achievedBase, pending, perf,
          t.lastAchievementDate ? moment(t.lastAchievementDate).format('YYYY-MM-DD') : '',
        ];

        const dailyEntries = t.dailyEntries || [];

        if (dailyEntries.length === 0) {
          // Target has no daily entries — emit one row with target context.
          const aCols = assigneeFirstRow ? aSummaryFull : blankASummary;
          dataRows.push(['', ...aCols, ...tCtxFull, ...blankValues]);
          assigneeFirstRow = false;
        } else {
          let targetFirstRow = true;
          dailyEntries.forEach(d => {
            const date = d.ach_date ? moment(d.ach_date).format('YYYY-MM-DD') : '';
            const aCols = assigneeFirstRow ? aSummaryFull : blankASummary;
            const tCols = targetFirstRow ? tCtxFull : blankTCtx;
            if (targetType === 'number') {
              dataRows.push([date, ...aCols, ...tCols, Number(d.number) || 0]);
            } else {
              const rnc = Number(d.rnc) || 0;
              const rec = Number(d.rec) || 0;
              dataRows.push([date, ...aCols, ...tCols, rnc, rec, rnc + rec]);
            }
            assigneeFirstRow = false;
            targetFirstRow = false;
          });
        }
      });
    });

    const sheet = XLSX.utils.aoa_to_sheet([...summary, headers, ...dataRows]);
    // Best-effort column widths so the sheet is readable on first open
    sheet['!cols'] = headers.map((h) => ({
      wch: Math.max(12, Math.min(28, String(h).length + 4))
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'HQ Overview');
    XLSX.writeFile(wb, 'HQ_Overview_' + moment().format('YYYY-MM-DD_HHmmss') + '.xlsx');
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">
          <i className="fas fa-globe me-2"></i>
          HQ Overview
          <span className="badge bg-secondary ms-2" title="Reports aggregate across currencies using your company's base currency">
            Base: {baseCurrency}
          </span>
        </h3>
      </div>

      <div className="card mb-4 dashboard-filter">
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2">
                <i className="fas fa-bullseye me-1"></i>Target Type
              </label>
              <select className="form-select form-select-sm" value={targetType} onChange={(e) => setTargetType(e.target.value)} disabled={loading}>
                <option value="amount">Target Amount</option>
                <option value="number">Target Number</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2" title="Targets whose window overlaps this date are included">
                <i className="fas fa-calendar me-1"></i>Target Window From
              </label>
              <input type="date" className="form-control form-control-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} disabled={loading} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2" title="Targets whose window overlaps this date are included">
                <i className="fas fa-calendar me-1"></i>Target Window To
              </label>
              <input type="date" className="form-control form-control-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} disabled={loading} />
            </div>
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-outline-secondary btn-sm"
                onClick={() => { setFromDate(''); setToDate(''); setTargetType('amount'); }}
                disabled={loading}>
                <i className="fas fa-redo-alt me-1"></i>Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>{error}
        </div>
      )}
      {emptyMessage && !error && !loading && (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>{emptyMessage}
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-primary">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Company Total Target</h6>
              <h3 className="card-title text-primary mb-0">{formatValue(companyTotals.target)}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-success">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Total Achievement</h6>
              <h3 className="card-title text-success mb-0">{formatValue(companyTotals.achievement)}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-warning">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Total Balance</h6>
              <h3 className="card-title text-warning mb-0">{formatValue(companyTotals.balance)}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-info">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Overall Achievement %</h6>
              <h3 className="card-title text-info mb-0">{companyPct}%</h3>
              <div className="small text-muted">across {companyTotals.assigneesCount} assignees</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0"><i className="fas fa-users me-2"></i>Per-Assignee Overview</h5>
          <button
            className="btn btn-success btn-sm"
            onClick={exportToExcel}
            disabled={rows.length === 0 || loading}
            title="Download Per-Assignee Overview, Targets Detail, and Daily Entries as Excel"
          >
            <i className="fas fa-file-excel me-2"></i>Download Excel
          </button>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Grid
              data={process(rows, dataState)}
              pageable={{ buttonCount: 5, info: true, pageSizes: [10, 25, 50, 100] }}
              sortable
              onDataStateChange={(e) => setDataState(e.dataState)}
              {...dataState}>
              <GridColumn field="name" title="Assignee" cell={NameCell} width="250px" />
              <GridColumn field="totalTarget" title={"Total Target (" + baseCurrency + ")"} cell={ValueCell} width="200px" />
              <GridColumn field="totalAchievement" title={"Total Achievement (" + baseCurrency + ")"} cell={ValueCell} width="220px" />
              <GridColumn field="achievementPercentage" title="Achievement %" cell={PercentCell} width="150px" />
              <GridColumn field="balance" title={"Balance (" + baseCurrency + ")"} cell={ValueCell} width="180px" />
              <GridColumn field="targetsCount" title="Drill Down" cell={DrillCell} width="180px" />
            </Grid>
          )}
        </div>
      </div>

      {drillDown && (
        <DrillDownModal
          assignee={drillDown}
          baseCurrency={baseCurrency}
          targetType={targetType}
          currencySymbol={currencySymbol}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
}

function DrillDownModal({ assignee, baseCurrency, targetType, currencySymbol, onClose }) {
  const [expandedDaily, setExpandedDaily] = useState({});
  const toggleDaily = (id) => setExpandedDaily((prev) => ({ ...prev, [id]: !prev[id] }));

  const fmt = (v) => {
    if (targetType === 'amount') {
      return currencySymbol + new Intl.NumberFormat('en-IN').format(v || 0) + " " + baseCurrency;
    }
    return new Intl.NumberFormat('en-IN').format(v || 0);
  };
  const fmtNumber = (v) => new Intl.NumberFormat('en-IN').format(Number(v) || 0);

  // Targets Breakdown popup Excel export.
  //
  // Single flat sheet so the user can filter/sort/pivot directly. Each row
  // carries the full target context + a leaf daily entry. Targets with no
  // daily entries still produce one row so they remain visible.
  //
  //   Target | Period | Window Start | Window End | Currency
  //   | Assigned (native) | Achieved (native)
  //   | Assigned (base) | Achieved (base) | Pending (base) | Performance %
  //   | Last Achievement Date
  //   | Daily Date | RNC | REC | Daily Total       (for amount)
  //   | Daily Date | Count                          (for number)
  const exportPopupToExcel = () => {
    const summary = [
      ['Targets Breakdown - ' + (assignee.name || '')],
      ['Assignee', assignee.name || ''],
      ['Email', assignee.email || ''],
      ['Target Type', targetType === 'amount' ? 'Amount' : 'Number'],
      ['Base Currency', baseCurrency],
      ['Total Target (' + baseCurrency + ')', Number(assignee.totalTarget) || 0],
      ['Total Achievement (' + baseCurrency + ')', Number(assignee.totalAchievement) || 0],
      ['Total Balance (' + baseCurrency + ')', (Number(assignee.totalTarget) || 0) - (Number(assignee.totalAchievement) || 0)],
      ['Achievement %', Number(assignee.achievementPercentage) || 0],
      ['Targets Count', (assignee.targets || []).length],
      ['Generated At', moment().format('YYYY-MM-DD HH:mm:ss')],
      [],
    ];

    // Column order: Daily Date first, then full parent context, then the
    // daily-entry numeric columns at the end.
    const headers = [
      'Daily Date',
      'Target', 'Period', 'Window Start', 'Window End', 'Currency',
      'Assigned (native)', 'Achieved (native)',
      'Assigned (' + baseCurrency + ')',
      'Achieved (' + baseCurrency + ')',
      'Pending (' + baseCurrency + ')',
      'Performance %', 'Last Achievement Date',
    ];
    if (targetType === 'number') {
      headers.push('Count');
    } else {
      headers.push('RNC', 'REC', 'Daily Total');
    }

    // Target context columns are only populated on the FIRST row of each
    // target block. Subsequent daily-entry rows leave those columns blank
    // so the sheet reads as a grouped report.
    const blankTCtx = ['', '', '', '', '', '', '', '', '', '', '', ''];
    const blankValues = targetType === 'number' ? [''] : ['', '', ''];

    const dataRows = [];
    (assignee.targets || []).forEach(t => {
      const assignedBase = Number(t.assignedBase) || 0;
      const achievedBase = Number(t.achievedBase) || 0;
      const pending = assignedBase - achievedBase;
      const perf = assignedBase > 0
        ? Number(((achievedBase / assignedBase) * 100).toFixed(2))
        : 0;
      const tCtxFull = [
        t.title || '', t.period || '',
        t.startDate ? moment(t.startDate).format('YYYY-MM-DD') : '',
        t.endDate ? moment(t.endDate).format('YYYY-MM-DD') : '',
        t.currency || '',
        Number(t.assignedNative) || 0,
        Number(t.achievedNative) || 0,
        assignedBase, achievedBase, pending, perf,
        t.lastAchievementDate ? moment(t.lastAchievementDate).format('YYYY-MM-DD') : '',
      ];

      const dailyEntries = t.dailyEntries || [];

      if (dailyEntries.length === 0) {
        dataRows.push(['', ...tCtxFull, ...blankValues]);
      } else {
        let targetFirstRow = true;
        dailyEntries.forEach(d => {
          const date = d.ach_date ? moment(d.ach_date).format('YYYY-MM-DD') : '';
          const tCols = targetFirstRow ? tCtxFull : blankTCtx;
          if (targetType === 'number') {
            dataRows.push([date, ...tCols, Number(d.number) || 0]);
          } else {
            const rnc = Number(d.rnc) || 0;
            const rec = Number(d.rec) || 0;
            dataRows.push([date, ...tCols, rnc, rec, rnc + rec]);
          }
          targetFirstRow = false;
        });
      }
    });

    const sheet = XLSX.utils.aoa_to_sheet([...summary, headers, ...dataRows]);
    sheet['!cols'] = headers.map((h) => ({
      wch: Math.max(12, Math.min(28, String(h).length + 4))
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Targets Breakdown');
    const safeName = (assignee.name || 'assignee').replace(/[^a-zA-Z0-9_-]+/g, '_');
    XLSX.writeFile(wb, 'Targets_Breakdown_' + safeName + '_' + moment().format('YYYY-MM-DD_HHmmss') + '.xlsx');
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-user me-2"></i>{assignee.name} - Targets Breakdown
            </h5>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={exportPopupToExcel}
                disabled={!assignee.targets || assignee.targets.length === 0}
                title="Download targets + daily entries as Excel"
              >
                <i className="fas fa-file-excel me-2"></i>Download Excel
              </button>
              <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
            </div>
          </div>
          <div className="modal-body">
            <div className="row mb-3">
              <div className="col-md-4">
                <strong>Total Target:</strong> {fmt(assignee.totalTarget)}
              </div>
              <div className="col-md-4">
                <strong>Total Achievement:</strong> {fmt(assignee.totalAchievement)}
              </div>
              <div className="col-md-4">
                <strong>Achievement %:</strong> {assignee.achievementPercentage}%
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Target</th>
                    <th>Period</th>
                    <th>Window</th>
                    <th>Currency</th>
                    <th className="text-end">Assigned (native)</th>
                    <th className="text-end">Achieved (native)</th>
                    <th className="text-end">Assigned ({baseCurrency})</th>
                    <th className="text-end">Achieved ({baseCurrency})</th>
                    <th className="text-end">Pending ({baseCurrency})</th>
                    <th className="text-end">Performance %</th>
                    <th>Last Achievement Date</th>
                    <th>Daily Entries</th>
                  </tr>
                </thead>
                <tbody>
                  {(assignee.targets || []).map((t) => {
                    const assignedBase = Number(t.assignedBase) || 0;
                    const achievedBase = Number(t.achievedBase) || 0;
                    const pending = assignedBase - achievedBase;
                    const perf = assignedBase > 0 ? Number(((achievedBase / assignedBase) * 100).toFixed(2)) : 0;
                    const perfColor = perf >= 100 ? 'text-success' : perf >= 75 ? 'text-info' : perf >= 50 ? 'text-warning' : 'text-danger';
                    const dailyEntries = t.dailyEntries || [];
                    const isOpen = !!expandedDaily[t.id];

                    const dailyTotals = dailyEntries.reduce((acc, d) => ({
                      rnc: acc.rnc + (Number(d.rnc) || 0),
                      rec: acc.rec + (Number(d.rec) || 0),
                      number: acc.number + (Number(d.number) || 0),
                    }), { rnc: 0, rec: 0, number: 0 });

                    return (
                      <React.Fragment key={t.id}>
                        <tr>
                          <td className="fw-bold">{t.title}</td>
                          <td>{t.period}</td>
                          <td className="small">
                            {t.startDate ? moment(t.startDate).format('DD-MM-YYYY') : ''} -&gt; {t.endDate ? moment(t.endDate).format('DD-MM-YYYY') : ''}
                          </td>
                          <td>{t.currency}</td>
                          <td className="text-end">{fmtNumber(t.assignedNative)}</td>
                          <td className="text-end">{fmtNumber(t.achievedNative)}</td>
                          <td className="text-end">{fmt(assignedBase)}</td>
                          <td className="text-end">{fmt(achievedBase)}</td>
                          <td className="text-end">{fmt(pending)}</td>
                          <td className={"text-end fw-bold " + perfColor}>{perf}%</td>
                          <td>{t.lastAchievementDate ? moment(t.lastAchievementDate).format('DD-MM-YYYY') : <span className="text-muted">-</span>}</td>
                          <td className="text-center">
                            {dailyEntries.length === 0 ? (
                              <span className="text-muted">0</span>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info"
                                onClick={() => toggleDaily(t.id)}
                                title={isOpen ? 'Hide daily achievements' : 'Show daily achievements'}
                              >
                                <i className={"fas " + (isOpen ? 'fa-chevron-up' : 'fa-chevron-down') + " me-1"}></i>
                                {dailyEntries.length}
                              </button>
                            )}
                          </td>
                        </tr>
                        {dailyEntries.length > 0 && isOpen && (
                          <tr>
                            <td colSpan={12} className="p-0">
                              <div className="bg-light p-3 hq-daily-wrap">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <strong>
                                    <i className="fas fa-calendar-day me-2"></i>
                                    Daily achievements
                                    <span className="badge bg-secondary ms-2">{dailyEntries.length}</span>
                                  </strong>
                                </div>
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered bg-white mb-0 hq-daily-table">
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{ width: '60px' }}>#</th>
                                        <th>Date</th>
                                        {targetType === 'number' ? (
                                          <th className="text-end">Count</th>
                                        ) : (
                                          <>
                                            <th className="text-end">RNC</th>
                                            <th className="text-end">REC</th>
                                            <th className="text-end">Daily Total</th>
                                          </>
                                        )}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {dailyEntries.map((d, i) => {
                                        const rnc = Number(d.rnc) || 0;
                                        const rec = Number(d.rec) || 0;
                                        const num = Number(d.number) || 0;
                                        return (
                                          <tr key={t.id + '-d-' + i}>
                                            <td className="text-muted">{i + 1}</td>
                                            <td>{moment(d.ach_date).format('DD-MM-YYYY')}</td>
                                            {targetType === 'number' ? (
                                              <td className="text-end">{fmtNumber(num)}</td>
                                            ) : (
                                              <>
                                                <td className="text-end">{fmtNumber(rnc)}</td>
                                                <td className="text-end">{fmtNumber(rec)}</td>
                                                <td className="text-end fw-bold">{fmtNumber(rnc + rec)}</td>
                                              </>
                                            )}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot className="table-light">
                                      <tr>
                                        <td colSpan={2} className="text-end fw-bold">Total</td>
                                        {targetType === 'number' ? (
                                          <td className="text-end fw-bold">{fmtNumber(dailyTotals.number)}</td>
                                        ) : (
                                          <>
                                            <td className="text-end fw-bold">{fmtNumber(dailyTotals.rnc)}</td>
                                            <td className="text-end fw-bold">{fmtNumber(dailyTotals.rec)}</td>
                                            <td className="text-end fw-bold">{fmtNumber(dailyTotals.rnc + dailyTotals.rec)}</td>
                                          </>
                                        )}
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {(!assignee.targets || assignee.targets.length === 0) && (
                    <tr><td colSpan={12} className="text-center text-muted">No targets</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              <i className="fas fa-times me-2"></i>Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
